import type { Carer, Home } from '@lmc/ts-common'
import {
  Button,
  Checkbox,
  CheckboxGroup,
  FormItem,
  Icon,
  IconType,
  Modal
} from '@lmc/ui-kit'
import { Form, Select } from 'antd'
import { showToast } from 'common/utils'
import { NAMESPACE } from 'locale'
import { reportingTranslations } from 'locale/reporting'
import { type FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { isGroupUser } from 'utils'
import { useGenerateCsv } from './hooks'
import { CSV_EXPORT_TYPE } from './utils'

type Props = {
  cards: { id: CSV_EXPORT_TYPE; title: string; desc?: string }[]
}

const getExportPath = (type?: CSV_EXPORT_TYPE) => {
  switch (type) {
    case CSV_EXPORT_TYPE.RESIDENT:
      return 'residents'
    case CSV_EXPORT_TYPE.TEAM_MEMBER:
      return 'users'
    case CSV_EXPORT_TYPE.CARE_PLAN:
      return 'careplans'
    case CSV_EXPORT_TYPE.RISK_ASSESSMENT:
      return 'riskassessments'
    case CSV_EXPORT_TYPE.LOGS:
      return 'logs'
    case CSV_EXPORT_TYPE.EMAR:
      return 'emarresidentmedications'
    case CSV_EXPORT_TYPE.OUTCOME_AND_GOALS:
      return 'goals'
    default:
      return ''
  }
}

export const DownloadBulk: FC<Props> = ({ cards }) => {
  const { t } = useTranslation(NAMESPACE.REPORTING)

  const [isOpen, setIsOpen] = useState(false)

  const [form] = Form.useForm()

  const handleOpenModal = (open?: boolean) => {
    setIsOpen((prev) => open || !prev)
    if (!open) {
      form.resetFields()
    }
  }

  const user = useSelector(
    (state: { global: { user: Carer } }) => state.global.user
  )

  const isGroupDownload = isGroupUser(user.role)

  const homes = useSelector(
    (state: { data: { homes: Home[] } }) => state.data.homes
  )

  const homeOptions = homes?.map((home) => ({
    value: home?.id,
    label: home?.name
  }))

  const { isLoading, generate } = useGenerateCsv(CSV_EXPORT_TYPE.BULK, () => {
    handleOpenModal(false)
    showToast({
      title: `An email with the CSV will be sent to ${user.email}. At peak times this may take a while.`
    })
  })

  const handleBulkGenerate = (values: object) => {
    generate(values)
    form.resetFields()
  }

  return (
    <>
      <Modal
        isModalVisible={isOpen}
        localisation={{
          title: t(reportingTranslations.BULK_DOWNLOAD),
          confirmText: t(reportingTranslations.DOWNLOAD_CSVS)
        }}
        onConfirm={form.submit}
        onCancel={() => {
          handleOpenModal?.()
          form.resetFields()
        }}
        confirmButtonProps={{
          variant: 'text',
          isLoading,
          icon: <Icon customIcon={IconType.BulkDownloadIcon} />
        }}
        closeButton
        noPadding
      >
        <Form form={form} layout="vertical" onFinish={handleBulkGenerate}>
          <StyledFormItem
            label="Select which CSV reports to download"
            name="bulks"
            rules={[
              {
                required: true
              }
            ]}
          >
            <CheckboxGroup>
              <CheckboxVertical>
                {cards
                  ?.filter(
                    (item) =>
                      ![
                        CSV_EXPORT_TYPE.UPCOMING_ROTA,
                        CSV_EXPORT_TYPE.ROSTERING_HISTORY
                      ]?.includes(item.id)
                  )
                  .map((card) => (
                    <Checkbox value={getExportPath(card.id)} key={card.id}>
                      {card.title}
                    </Checkbox>
                  ))}
              </CheckboxVertical>
            </CheckboxGroup>
          </StyledFormItem>
          {isGroupDownload && (
            <FormItem
              label={t(reportingTranslations.SITES)}
              name="sites"
              extra={t(reportingTranslations.SITES_EXTRA)}
              rules={[
                {
                  required: true
                }
              ]}
            >
              <Select options={homeOptions} mode="multiple" />
            </FormItem>
          )}
        </Form>
      </Modal>
      <Button variant="primary" onClick={() => handleOpenModal()}>
        {t(reportingTranslations.BULK_DOWNLOAD)}
        <Icon customIcon={IconType.BulkDownloadIcon} />
      </Button>
    </>
  )
}

const StyledFormItem = styled(FormItem)`
  .ant-form-item-required {
    white-space: nowrap;
  }
`

const CheckboxVertical = styled.div`
  display: flex !important;
  flex-direction: column !important;
  gap: 10px;
  margin-top: 10px;
  margin-bottom: 20px;
`
